import axios from 'axios';
import { useContext, useEffect, useReducer, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import ListGroup from 'react-bootstrap/ListGroup';
import Card from 'react-bootstrap/Card';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
// import Rating from '../components/Rating';
import { Helmet } from 'react-helmet-async';
import LoadingBox from '../components/LoadingBox';
import MessageBox from '../components/MessageBox';
import { getError } from '../utils';
import { Store } from '../Store';
import { getVariantSizes, getVariantPrice, getStockTotal } from '../productInventory';
import { toast } from 'react-toastify';

const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loading: true };
    case 'FETCH_SUCCESS':
      return { ...state, product: action.payload, loading: false };
    case 'FETCH_FAIL':
      return { ...state, loading: false, error: action.payload };
    default:
      return state;
  }
};

function ProductScreen() {
  const navigate = useNavigate();
  const params = useParams();
  const { slug } = params;

  const [{ loading, error, product }, dispatch] = useReducer(reducer, {
    product: [],
    loading: true,
    error: '',
  });

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      dispatch({ type: 'FETCH_REQUEST' });
      try {
        const result = await axios.get(`/api/products/slug/${slug}`);
        dispatch({ type: 'FETCH_SUCCESS', payload: result.data });
      } catch (error) {
        dispatch({ type: 'FETCH_FAIL', payload: getError(error) });
      }
    };
    fetchData();
  }, [slug]);

  useEffect(() => {
    const color = product.colors?.[0]?.name || null;
    setSelectedColor(color);
    setSelectedSize(
      getVariantSizes(product, color).some((entry) => entry.size === 'One Size' && entry.countInStock > 0)
        ? 'One Size' : null
    );
    setCurrentImageIndex(0);
  }, [product]);

  const { state, dispatch: ctxDispatch } = useContext(Store);
  const { cart } = state;

  const addToCartHandler = async () => {
    try {
      const { data } = await axios.get(`/api/products/${product._id}`);
      const selectedSizeData = getVariantSizes(data, selectedColor).find(
        (size) => size.size === selectedSize
      );
      if (!selectedSizeData || selectedSizeData.countInStock === 0) {
        window.alert('Sorry. Product is out of stock');
        return;
      }

      const existItem = cart.cartItems.find(
        (item) =>
          item._id === product._id &&
          item.size === selectedSize &&
          item.color === selectedColor
      );
      const quantity = existItem ? existItem.quantity + 1 : 1;

      if (selectedSizeData.countInStock < quantity) {
        window.alert('Sorry. Product is out of stock');
        return;
      }

      ctxDispatch({
        type: 'CART_ADD_ITEM',
        payload: {
          ...data,
          countInStock: selectedSizeData.countInStock,
          quantity,
          size: selectedSize,
          color: selectedColor,
          price: getVariantPrice(data, selectedSizeData),
        },
      });
      navigate('/cart');
    } catch (err) {
      toast.error(getError(err));
    }
  };

  const availableSizes = getVariantSizes(product, selectedColor);
  const activeStock = getStockTotal(availableSizes);
  const selectedSizeEntry = availableSizes.find((entry) => entry.size === selectedSize);
  const priceDisplay = (
    <>
      {selectedSizeEntry?.price == null && product.salePrice != null && (
        <>
          <s style={{ color: '#999' }}>{product.price} EGP</s>{' '}
        </>
      )}
      <strong>{getVariantPrice(product, selectedSizeEntry)} EGP</strong>
    </>
  );
  const selectedColorImages = product.colors?.find(
    (color) => color.name === selectedColor
  )?.images;
  const activeImages =
    selectedColorImages?.length ? selectedColorImages : product.image ?? [];

  const handlePrevImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex > 0 ? prevIndex - 1 : prevIndex
    );
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex < activeImages.length - 1 ? prevIndex + 1 : prevIndex
    );
  };

  const handleSelectSize = (size) => {
    setSelectedSize(size);
  };

  const handleSelectColor = (colorName) => {
    setSelectedColor(colorName);
    setSelectedSize(
      getVariantSizes(product, colorName).some((entry) => entry.size === 'One Size' && entry.countInStock > 0)
        ? 'One Size' : null
    );
    setCurrentImageIndex(0);
  };

  return (
    <div>
      {loading ? (
        <LoadingBox />
      ) : error ? (
        <MessageBox variant="danger">{error}</MessageBox>
      ) : (
        <div>
          <Row>
            <Col md={6} style={{ textAlign: 'center' }}>
              <img
                className="img-large"
                src={activeImages[currentImageIndex]}
                alt={product.name}
              />
              <div className="d-flex justify-content-between mt-2">
                <Button
                  variant="primary"
                  onClick={handlePrevImage}
                  disabled={currentImageIndex === 0}
                >
                  Previous
                </Button>
                <Button
                  variant="primary"
                  onClick={handleNextImage}
                  disabled={currentImageIndex === activeImages.length - 1}
                >
                  Next
                </Button>
              </div>
            </Col>
            <Col md={3}>
              <ListGroup variant="flush">
                <ListGroup.Item>
                  <Helmet>
                    <title>{product.name}</title>
                  </Helmet>
                  <h1>{product.name}</h1>
                </ListGroup.Item>
                <ListGroup.Item>
                  {/* <Rating
                    rating={product.rating}
                    numReviews={product.numReviews}
                  /> */}
                  {product.description}
                </ListGroup.Item>
                <ListGroup.Item>
                  {priceDisplay}
                </ListGroup.Item>
              </ListGroup>
            </Col>
            <Col md={3}>
              <Card>
                <Card.Body>
                  <ListGroup variant="flush">
                    <ListGroup.Item>
                      <Row>
                        <Col md={4}>Price</Col>
                        <Col md={8}>
                          {priceDisplay}
                        </Col>
                      </Row>
                    </ListGroup.Item>
                    {product.colors && product.colors.length > 0 && (
                      <ListGroup.Item>
                        <Row>
                          <Col md={4}>Color</Col>
                          <Col md={8}>
                            {product.colors.map((c) => (
                              <Button
                                key={c.name}
                                onClick={() => handleSelectColor(c.name)}
                                variant={
                                  c.name === selectedColor
                                    ? 'dark'
                                    : 'outline-dark'
                                }
                                className="m-1 btn-sm"
                              >
                                {c.name}
                              </Button>
                            ))}
                          </Col>
                        </Row>
                      </ListGroup.Item>
                    )}
                    <ListGroup.Item>
                      <Row>
                        <Col md={4}>Size</Col>
                        <Col md={8}>
                          {availableSizes.map((size) => (
                            <Button
                              key={size.size}
                              onClick={() => handleSelectSize(size.size)}
                              disabled={size.countInStock === 0}
                              variant={
                                size.size === selectedSize
                                  ? 'primary'
                                  : 'outline-primary'
                              }
                              className="m-1 btn-sm"
                            >
                              {size.size}
                            </Button>
                          ))}
                        </Col>
                      </Row>
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <Row>
                        <Col md={4}>Status</Col>
                        <Col md={8}>
                          {activeStock > 0 ? (
                            <Badge bg="success">In Stock</Badge>
                          ) : (
                            <Badge bg="danger">Unavailable</Badge>
                          )}
                        </Col>
                      </Row>
                    </ListGroup.Item>
                    {activeStock > 0 && (
                      <ListGroup.Item>
                        <div className="d-grid">
                          <Button
                            onClick={addToCartHandler}
                            variant="primary"
                            disabled={!selectedSize}
                          >
                            Add to Cart
                          </Button>
                        </div>
                      </ListGroup.Item>
                    )}
                  </ListGroup>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>
      )}
    </div>
  );
}

export default ProductScreen;
